import { Button, Input, Tooltip } from 'antd'
import IconTooltip from 'components/icons/IconTooltip'
import { ReactNode } from 'react'
import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form'
import { NumericFormat, NumericFormatProps } from 'react-number-format'
import { infoNodeComponents, TInfoNodeCb } from '../helpers/InfoNode'
import FormMessage from '../helpers/FormMessage'
import { cn } from 'utils/common'

interface IBaseInputNumberProps<T extends FieldValues>
  extends Pick<NumericFormatProps, 'prefix' | 'placeholder' | 'disabled' | 'suffix'>,
    Pick<NumericFormatProps, 'onValueChange'> {
  name: Path<T>
  control: Control<T>
  label?: string
  max?: number
  min?: number
  className?: string
  infoNode?: ReactNode | TInfoNodeCb
  warning?: FieldError & { type: 'warning' }
}

const BaseInputNumber = <T extends FieldValues = FieldValues>(props: IBaseInputNumberProps<T>) => {
  const tooltipTitle = typeof props.infoNode === 'function' ? props.infoNode(infoNodeComponents) : props.infoNode
  return (
    <Controller
      name={props.name}
      control={props.control}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        return (
          <div className='relative'>
            {props.label && (
              <label className='text-sm text-neutral-700 font-medium flex items-center' htmlFor={props.name}>
                {props.label}
                {!!tooltipTitle && (
                  <Tooltip title={tooltipTitle}>
                    <Button
                      className='w-4 min-w-4 h-4 min-h-4 border-none ml-1'
                      shape='circle'
                      icon={<IconTooltip />}
                    />
                  </Tooltip>
                )}
              </label>
            )}
            <NumericFormat
              className={cn('mt-1', props.className)}
              customInput={Input}
              disabled={props.disabled}
              allowNegative={false}
              decimalScale={2}
              thousandSeparator
              status={error ? 'error' : props.warning ? 'warning' : ''}
              value={value ?? ''}
              placeholder={props.placeholder}
              prefix={props.prefix}
              suffix={props.suffix}
              isAllowed={(values) => {
                const { floatValue } = values
                if (floatValue === undefined) return true
                const min = props.min || 0
                const max = props.max && value === props.max ? props.max : Number.MAX_SAFE_INTEGER

                return floatValue >= min && floatValue <= max
              }}
              onValueChange={(values, sourceInfo) => {
                props.onValueChange?.(values, sourceInfo)
                if (values.value === '') {
                  onChange(null)
                } else {
                  const value =
                    props.max && values.floatValue && props.max < values.floatValue ? props.max : values.floatValue
                  onChange(value)
                }
              }}
              valueIsNumericString={true}
            />
            <FormMessage error={error} warning={props.warning} />
          </div>
        )
      }}
    />
  )
}

export default BaseInputNumber
